import { UncontrolledTooltip } from 'reactstrap';
import { TYPE_BEAT_ENUM } from '@/shared/config';
import ThumbnailBookmark from '../thumbnailBookmark';
import useBookmarkPreviewStrip from './useBookmarkPreviewStrip';

export type BookmarkPreviewStripProps = {
  id: string;
  onClickSelectEvent: (bookmark: any, index: any) => void;
  isSelected?: boolean;
  isActive?: boolean;
  channels: any;
  highPass: any;
  width: number;
  height: number;
  gain: any;
  timezoneOffset: number;
  startReport: any;
  bookmark: any;
  ecgDataMap: any;
  customOffset?: any;
  index: any;
  // stripBookmarks.tsx also passes these, but the component never reads them - pre-existing.
};

/**
 * Renders one ECG bookmark thumbnail in the bookmark strip list, showing its centre time and notes. Marks the thumbnail with the deleted badge when useBookmarkPreviewStrip reports a pending delete and adds a tooltip saying the change has not been applied.
 *
 * @param props - Thumbnail inputs including the bookmark, its element id, gain, size, high-pass setting, timezone offset, report start and selection flags.
 * @returns The bookmark thumbnail, optionally followed by an unapplied-change tooltip.
 */
const BookmarkPreviewStrip = ({
  id,
  onClickSelectEvent,
  isSelected,
  isActive,
  channels: channelsProp,
  highPass,
  width,
  height,
  gain,
  timezoneOffset,
  startReport,
  bookmark,
  ecgDataMap,
  customOffset,
  index,
}: BookmarkPreviewStripProps) => {
  const { isDeleted, ecgData, beatMarkers, hesBeatStatus, channels, onClick } =
    useBookmarkPreviewStrip({
      onClickSelectEvent,
      channels: channelsProp,
      bookmark,
      ecgDataMap,
      customOffset,
      index,
    });

  return (
    <>
      <ThumbnailBookmark
        id={id}
        onClickSelect={onClick as any}
        isSelected={isSelected}
        isChanged={isDeleted}
        isActive={isActive}
        ecgData={ecgData}
        beatMarkers={beatMarkers}
        hesBeatStatus={hesBeatStatus}
        channels={channels}
        highPass={highPass}
        width={width}
        height={height}
        gain={gain}
        time={bookmark.center}
        typeRight={isDeleted ? TYPE_BEAT_ENUM.DELETED : null}
        timezoneOffset={timezoneOffset}
        startReport={startReport}
        notes={bookmark.notes}
      />
      {isDeleted && (
        <UncontrolledTooltip
          className="custom-tooltip --thumbnail"
          target={id}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          The change has not been applied
        </UncontrolledTooltip>
      )}
    </>
  );
};

export default BookmarkPreviewStrip;
